// PriorityMark — a 4-notch signal bar, deliberately quiet.
//
// Translated from prototype/primitives.jsx `PriorityMark`. A coloured chip per
// priority makes a 25-row queue a rainbow and competes with status, so low and
// medium stay grey (--pri-low / --pri-medium) and only high/urgent take colour.
// The row-edge stripe (see PRIORITY_STRIPE below) is likewise high/urgent only.
import React from 'react';
import { Box } from '@mui/material';
import { TicketPriority } from '../types';

interface PriorityMeta {
  label: string;
  notches: number;
  /** true = this priority earns colour (text + row stripe); false = stays grey */
  tokenised: boolean;
}

const PRIORITY: Record<TicketPriority, PriorityMeta> = {
  low: { label: 'Low', notches: 1, tokenised: false },
  medium: { label: 'Medium', notches: 2, tokenised: false },
  high: { label: 'High', notches: 3, tokenised: true },
  urgent: { label: 'Urgent', notches: 4, tokenised: true }
};

/**
 * Row-edge stripe colour for the queue, or undefined when the priority does
 * not earn one. Feed into the `--stripe` custom property on a row carrying the
 * `hd-row-stripe` class (see tokens.css).
 */
export function priorityStripe(priority: TicketPriority): string | undefined {
  return PRIORITY[priority].tokenised ? `var(--pri-${priority})` : undefined;
}

export function priorityLabel(priority: TicketPriority): string {
  return PRIORITY[priority].label;
}

interface PriorityMarkProps {
  priority: TicketPriority;
  withLabel?: boolean;
  /** Height of the tallest notch, px. */
  size?: number;
}

export default function PriorityMark({ priority, withLabel, size = 12 }: PriorityMarkProps) {
  const meta = PRIORITY[priority];
  const colour = `var(--pri-${priority})`;

  return (
    <Box
      component="span"
      title={`Priority: ${meta.label}`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '1.5px', height: size }}>
        {[1, 2, 3, 4].map((notch) => (
          <Box
            key={notch}
            component="span"
            sx={{
              width: '2.5px',
              height: size * (0.34 + notch * 0.165),
              borderRadius: '1px',
              backgroundColor: notch <= meta.notches ? colour : 'var(--hairline-strong)'
            }}
          />
        ))}
      </Box>
      {withLabel ? (
        <Box
          component="span"
          sx={{
            fontSize: 12.5,
            color: meta.tokenised ? colour : 'var(--ink-2)',
            fontWeight: meta.tokenised ? 600 : 500
          }}
        >
          {meta.label}
        </Box>
      ) : null}
    </Box>
  );
}
